import React, { useState, useEffect } from "react";
import { api } from "../api/client";
import { timePattern, timeRegex } from "../pages/AdminDashboard";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function AuthPage({ onAuth }) {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "passenger",
    contact: "",
    stop: "",
    gender: "male",
  });
  const [schedule, setSchedule] = useState(
    DAYS.map((day) => ({ day, off: true, morning: "", evening: "" }))
  );
  const [error, setError] = useState("");
  const [stops, setStops] = useState([]);

  useEffect(() => {
    const loadStops = async () => {
      try {
        const r = await api("/stops");
        setStops(r.stops);
      } catch (err) {
        console.error("Failed to load stops");
      }
    };
    loadStops();
  }, []);

  const switchMode = () => {
    setMode((m) => (m === "login" ? "register" : "login"));
    setError("");
  };

  const validateSchedule = () => {
    if (form.role !== "passenger") return true;

    for (const day of schedule) {
      if (!day.off) {
        if (!day.morning || !timeRegex.test(day.morning)) {
          setError(`${day.day}: Invalid morning time format (use HH:mm 24-hr)`);
          return false;
        }
        if (!day.evening || !timeRegex.test(day.evening)) {
          setError(`${day.day}: Invalid evening time format (use HH:mm 24-hr)`);
          return false;
        }
        const [mh, mm] = day.morning.split(":").map(Number);
        const [eh, em] = day.evening.split(":").map(Number);
        const morningMins = mh * 60 + mm;
        const eveningMins = eh * 60 + em;
        if (eveningMins <= morningMins) {
          setError(`${day.day}: Evening time must be later than morning time`);
          return false;
        }
      }
    }
    return true;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");

    if (mode === "register" && !validateSchedule()) {
      return;
    }

    try {
      if (mode === "register") {
        const payload = { ...form };
        if (form.role === "passenger") {
          payload.schedule = schedule;
        }
        await api("/auth/register", { method: "POST", body: payload });
        alert("Registration submitted. Await approval.");
        setMode("login");
      } else {
        const data = await api("/auth/login", {
          method: "POST",
          body: { email: form.email, password: form.password },
        });
        if (data.token) {
          localStorage.setItem("token", data.token);
        }
        if (data.user && data.user.role === "admin") {
          localStorage.removeItem("tripCreateData");
        }
        onAuth(data.user);
      }
    } catch (err) {
      setError(err.message || "An error occurred");
    }
  };

  return (
    <div className="card" style={{ maxWidth: 400, margin: "2rem auto" }}>
      <h3>{mode === "login" ? "Login" : "Register"}</h3>
      {mode === "register" && (<p style={{
        color: "blue", fontSize: 14, marginTop: 10
        }}>Please carefully enter your details as you will be unable to change them later.</p>
      )}
      <form onSubmit={submit}>
        {mode === "register" && (
          <>
            <label>Name</label>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
            <label>Gender</label>
            <select
              value={form.gender}
              onChange={(e) => setForm({ ...form, gender: e.target.value })}
              required
            >
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
            <label>Contact Number</label>
            <input
              type="tel"
              value={form.contact}
              onChange={(e) => setForm({ ...form, contact: e.target.value })}
              required
            />
            <label>Role</label>
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
            >
              <option value="passenger">Passenger</option>
              <option value="driver">Driver</option>
            </select>
            {form.role === "passenger" && (
              <>
                <label>Stop</label>
                <select
                  value={form.stop}
                  onChange={(e) => setForm({ ...form, stop: e.target.value })}
                  required
                >
                  <option value="">Select Stop</option>
                  {stops.map((stop) => (
                    <option key={stop._id} value={stop._id}>
                      {stop.name}
                    </option>
                  ))}
                </select>
              </>
            )}
          </>
        )}
        <label>Email</label>
        <input
          type="email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          required
        />
        <label>
          Password{mode === "register" ? " (min 6 characters)" : ""}
        </label>
        <input
          type="password"
          value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
          required
        />
        {mode === "register" && form.role === "passenger" && (
          <>
            <label style={{ marginTop: 20 }}>Weekly Schedule</label>
            <div style={{ overflowX: "auto" }}>
              <table
              className="table"
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "12px",
                }}
              >
                <thead>
                  <tr>
                    <th style={{ border: "1px solid #ddd", padding: "8px" }}>
                      Day
                    </th>
                    <th style={{ border: "1px solid #ddd", padding: "8px" }}>
                      Off
                    </th>
                    <th style={{ border: "1px solid #ddd", padding: "8px" }}>
                      Morning Time
                    </th>
                    <th style={{ border: "1px solid #ddd", padding: "8px" }}>
                      Evening Time
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {schedule.map((day, idx) => (
                    <tr key={day.day}>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        {day.day}
                      </td>
                      <td
                        style={{
                          border: "1px solid #ddd",
                          padding: "8px",
                          textAlign: "center",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={day.off}
                          onChange={(e) => {
                            const newSchedule = [...schedule];
                            newSchedule[idx] = {
                              ...day,
                              off: e.target.checked,
                              morning: e.target.checked ? "" : day.morning,
                              evening: e.target.checked ? "" : day.evening,
                            };
                            setSchedule(newSchedule);
                          }}
                        />
                      </td>
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        <input
                          type="text"
                          placeholder={day.off ? "Day is off" : "HH:mm (24-hr)"}
                          pattern={timePattern}
                          value={day.morning}
                          disabled={day.off}
                          onChange={(e) => {
                            const newSchedule = [...schedule];
                            newSchedule[idx] = {
                              ...day,
                              morning: e.target.value,
                            };
                            setSchedule(newSchedule);
                          }}
                          required={!day.off}
                          style={{ width: "100px", padding: "4px", ...(day.off ? { backgroundColor: "#c2c2c2", cursor: "not-allowed" } : {}) }}
                        />
                      {!day.off &&
                        day.morning &&
                        !timeRegex.test(day.morning) && (
                          <span
                            style={{
                              color: "red",
                              fontSize: "0.8em",
                            }}
                          >
                            Invalid time (24-hr)
                          </span>
                        )}
                      </td>

                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        <input
                          type="text"
                          placeholder={day.off ? "Day is off" : "HH:mm (24-hr)"}
                          pattern={timePattern}
                          value={day.evening}
                          disabled={day.off}
                          onChange={(e) => {
                            const newSchedule = [...schedule];
                            newSchedule[idx] = {
                              ...day,
                              evening: e.target.value,
                            };
                            setSchedule(newSchedule);
                          }}
                          required={!day.off}
                          style={{ width: "100px", padding: "4px", ...(day.off ? { backgroundColor: "#c2c2c2", cursor: "not-allowed" } : {}) }}
                        />
                      {!day.off &&
                        day.evening &&
                        !timeRegex.test(day.evening) && (
                          <span
                            style={{
                              color: "red",
                              fontSize: "0.8em",
                            }}
                          >
                            Invalid time (24-hr)
                          </span>
                        )}
                      </td>

                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        {error && <div style={{ color: "red", fontSize: 12, textAlign: "center", marginTop: 10 }}>{error}</div>}
        <button
          className="btn"
          type="submit"
          style={{
            marginTop: 20,
            width: "100%",
            textAlign: "center",
            justifyContent: "center",
          }}
        >
          {mode === "login" ? "Login" : "Request Registration"}
        </button>
        {mode === "register" && (<p style={{
          color: "red", fontSize: 14, textAlign: "center", marginTop: 10
          }}><b>IMPORTANT:</b> Review all your details before submitting.</p>
        )}
      </form>
      <button
        style={{
          marginTop: 20,
          fontSize: 12,
          background: "none",
          border: "none",
          cursor: "pointer",
          textDecoration: "underline",
          color: "blue",
        }}
        onClick={switchMode}
      >
        {mode === "login"
          ? "Need an account? Register"
          : "Have an account? Login"}
      </button>
    </div>
  );
}
